import { addMonths, isSameMonth, parseISO } from 'date-fns';

import type { ApiAssetInfo } from '../../api/tags/tag.types';
import { ProductState } from '../../checkout/resourceSelection/redux/types';
import { isFreeTrialLevel } from '../../common/termedLevel/freeTrialLevel';
import { PeriodUnit, type TermedLevel, TermedLevelState } from '../../common/termedLevel/types';

export interface BookingOverviewStatistics {
    vehicles: {
        total: number;
        trucks: number;
        buses: number;
        vans: number;
    };
    inactiveActivations: {
        total: number;
        pending: number;
        expired: number;
        problem: number;
    };
    contracts: {
        total: number;
        freeTrial: number;
        noRenewalScheduled: number;
    };
    terminatingSoon: {
        total: number;
        thisMonth: number;
        nextMonth: number;
    };
}

const countAssetsByType = (assets: ApiAssetInfo[], type: string) =>
    assets.filter(asset => asset.type?.toLowerCase() === type).length;

const isProblemActivation = (contract: TermedLevel) =>
    contract.state === TermedLevelState.PENDING && contract.activationState === ProductState.INACTIVE;

const isExpiredActivation = (contract: TermedLevel) =>
    contract.state === TermedLevelState.ACTIVE && contract.activationState === ProductState.INACTIVE;

type ContractWithEndDate = TermedLevel & { endsAt: string };

const isTerminatingContract = (contract: TermedLevel): contract is ContractWithEndDate =>
    contract.endsAt !== undefined && contract.periodUnit !== PeriodUnit.DAY && contract.renewsToLevel === undefined;

export function getBookingOverviewStatistics(
    contracts: TermedLevel[],
    assets: ApiAssetInfo[],
    currentDate = new Date()
): BookingOverviewStatistics {
    const pendingActivations = contracts.filter(
        contract => contract.activationState === ProductState.PENDING_ACTIVATION
    ).length;
    const expiredActivations = contracts.filter(isExpiredActivation).length;
    const problemActivations = contracts.filter(isProblemActivation).length;

    const terminatingContracts = contracts.filter(isTerminatingContract);
    const nextMonth = addMonths(currentDate, 1);
    const terminatingThisMonth = terminatingContracts.filter(contract =>
        isSameMonth(parseISO(contract.endsAt), currentDate)
    ).length;
    const terminatingNextMonth = terminatingContracts.filter(contract =>
        isSameMonth(parseISO(contract.endsAt), nextMonth)
    ).length;

    return {
        vehicles: {
            total: assets.length,
            trucks: countAssetsByType(assets, 'truck'),
            buses: countAssetsByType(assets, 'bus'),
            vans: countAssetsByType(assets, 'van'),
        },
        inactiveActivations: {
            total: pendingActivations + expiredActivations + problemActivations,
            pending: pendingActivations,
            expired: expiredActivations,
            problem: problemActivations,
        },
        contracts: {
            total: contracts.length,
            freeTrial: contracts.filter(contract => isFreeTrialLevel(contract.level)).length,
            noRenewalScheduled: contracts.filter(contract => contract.renewsToLevel === undefined).length,
        },
        terminatingSoon: {
            total: terminatingThisMonth + terminatingNextMonth,
            thisMonth: terminatingThisMonth,
            nextMonth: terminatingNextMonth,
        },
    };
}
